import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { getTimezoneAbbr, parseDateTimeInUserTimezone } from 'src/utils/format-date-time';
import { getSetFacts } from 'src/components/lead-sidebar/displayed-lead-facts';

type Params = {
    lead: ILead;
    /** Display name for the lead, e.g. from LeadUtils.getLeadName */
    leadName: string;
    /**
     * If this lead is associated with a specific property
     */
    propertyName?: string;
    upcomingTour?: IShowing;
    /**
     * IANA timezone the tour times should be displayed in. Callers should resolve
     * this via `getEffectiveTourTimezone` (tour tz, else building tz, else user tz).
     */
    timezone?: string | null;
};

/**
 * Format a tour's start time as `ddd, MMM D, YYYY at h:mm A TZ`,
 * e.g. `Mon, Jul 13, 2026 at 11:00 AM PDT`.
 */
const formatTourDateTime = (tour: IShowing, timezone?: string | null): string => {
    const tourDate = parseDateTimeInUserTimezone(tour.start_time, timezone);
    const resolvedTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzAbbr = resolvedTimezone ? ` ${getTimezoneAbbr(resolvedTimezone)}` : '';
    return `${tourDate.format('ddd, MMM D, YYYY')} at ${tourDate.format('h:mm A')}${tzAbbr}`;
};

/**
 * Format lead details as plain text for sharing (e.g. copying to clipboard).
 * Sections with no data are omitted.
 */
export const formatLeadShareText = ({
    lead,
    leadName,
    propertyName,
    upcomingTour,
    timezone,
}: Params): string => {
    const sections: string[] = [`Lead Name: ${leadName}`];

    const contactLines: string[] = [];
    if (lead.email) {
        contactLines.push(`* email: ${lead.email}`);
    }
    if (lead.phone_number) {
        contactLines.push(`* phone: ${lead.phone_number}`);
    }
    if (contactLines.length > 0) {
        sections.push(['Contact Information', ...contactLines].join('\n'));
    }

    if (propertyName) {
        sections.push(`Property Interest\n${propertyName}`);
    }

    const factLines = getSetFacts(lead).map(({ label, value }) => `* ${label}: ${value}`);
    if (factLines.length > 0) {
        sections.push(['Lead Info', ...factLines].join('\n'));
    }

    const notes = lead.notes?.contents?.trim();
    if (notes) {
        sections.push(`Notes\n${notes}`);
    }

    if (upcomingTour) {
        sections.push(`Tour Info\nTime: ${formatTourDateTime(upcomingTour, timezone)}`);
    }

    return sections.join('\n\n');
};

export type OnsiteTeamRecipient = {
    email: string;
    name?: string;
};

/** Always CC'd on onsite team emails so leasing ops has a copy. */
export const ONSITE_TEAM_CC_EMAIL = 'leasing-ops@reffie.me';

type MailtoParams = Params & {
    /** Onsite team recipients (the property's contacts). Caller must ensure it is non-empty. */
    recipients: OnsiteTeamRecipient[];
};

/**
 * Build a `mailto:` deep link that opens the user's mail client (e.g. Gmail)
 * pre-addressed to the onsite team, with the lead share text as the body.
 */
export const getShareLeadMailtoLink = (params: MailtoParams): string => {
    const { recipients, leadName, upcomingTour, timezone } = params;

    const to = recipients
        .map(({ email, name }) => (name ? `${name} <${email}>` : email))
        .map(encodeURIComponent)
        .join(',');

    const tourSuffix = upcomingTour ? ` ${formatTourDateTime(upcomingTour, timezone)}` : '';
    const subject = `Tour Scheduled - ${leadName}${tourSuffix}`;
    const body = `Hi Team,\n\n${formatLeadShareText(params)}`;

    const query = `cc=${encodeURIComponent(ONSITE_TEAM_CC_EMAIL)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    return `mailto:${to}?${query}`;
};
